import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AssignIncidentDrawer from './assignIncidentDrawer';
import { Col, ConfigProvider, DatePicker, Row, Select, Spin, Tabs } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

import { IoArrowBack } from 'react-icons/io5';
import filter from '../../../assets/images/filter.png';
import TableWrapper from '../../../components/Grid';
import BasicDrawer from '../../../components/Drawer';
import Actiondropdown from '../../../components/DropDown/actiondropdown';
import { FaBan, FaEye, FaFilter } from 'react-icons/fa';
import { IoIosPower } from 'react-icons/io';
import { MdOutlineTaskAlt } from 'react-icons/md';
import LabelWithSelect from '../../../components/LabelWithSelect';
import { Colors, ApiCaller } from '../../../config';
import RangeDatePicker from '../../../components/Picker/RangePicker';
import moment from 'moment';
import { Button, TextField, ScrollEndMessage } from '../../../components';
import { ManageIncidentsAction } from '../../../store/actions';
import { useQuery } from 'react-query';
import { getAllIncidents, getAllProperties } from '../../../config/getApis';
import DateTimeComponent from '../../../config/util/DateAndTimeParser';
import dayjs from 'dayjs';
import { delay } from 'redux-saga/effects';
import useColors from '../../../hooks/useColors';

const ManageIncidents = () => {
    const { colorPrimary } = useColors();

    const { user, dateFormat } = useSelector((state) => state?.AppReducer);
    const userRole = user?.userRole[0];

    const { incidentData, loader } = useSelector(
        (state) => state?.ManageIncidentReducer,
    );
    const incidentsError = useSelector((state) => state?.ManageIncidentReducer?.incidentsError);
    const { data: incidentsDataTableRecords, paginate } = incidentData;
    console.log(incidentData);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const getSavedFilters = () => {
        try {
            const saved = sessionStorage.getItem('incidentFilters');
            return saved ? JSON.parse(saved) : {
                property: '', incidentType: '', status: '', level: '',
                startingDate: '', endingDate: '',
            };
        } catch {
            return { property: '', incidentType: '', status: '', level: '', startingDate: '', endingDate: '' };
        }
    };

    const savedFilters = getSavedFilters();
    const [isLevelDisabled, setIsLevelDisabled] = useState(savedFilters.status === 'pending');

    const [state, setState] = useState({
        search: '',
        limit: 20,
        page: 1,
        filterDrawer: savedFilters,
        sortBy: 'createdAt',
        sortOrder: 'desc',
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [pendingFilters, setPendingFilters] = useState(savedFilters);
    const [accumulatedIncidents, setAccumulatedIncidents] = useState([]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setState((prev) => ({
                ...prev,
                page: 1,
                search: searchTerm,
            }));
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    useEffect(() => {
        dispatch(ManageIncidentsAction.GetManageIncidents(state));
    }, [state.page, state.limit, state.search, state.sortBy, state.sortOrder, state.filterDrawer]);

    useEffect(() => {
        setAccumulatedIncidents([]);
    }, [state.search, state.sortBy, state.sortOrder, state.filterDrawer]);

    useEffect(() => {
        if (incidentsDataTableRecords) {
            if (state.page === 1) {
                setAccumulatedIncidents(incidentsDataTableRecords);
            } else {
                setAccumulatedIncidents((prev) => {
                    const existingIds = new Set(prev.map((i) => i.id));
                    const newIncidents = incidentsDataTableRecords.filter((i) => !existingIds.has(i.id));
                    return [...prev, ...newIncidents];
                });
            }
        }
    }, [incidentsDataTableRecords, state.page]);

    useEffect(() => {
        if (incidentsError) {
            setAccumulatedIncidents([]);
        }
    }, [incidentsError]);

    const hasMore = paginate ? (paginate.currentPage < paginate.lastPage) : true;

    useEffect(() => {
        const container = document.querySelector('.site-layout .ant-layout-content');
        if (!container) return;

        const handleScroll = () => {
            const isNearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
            if (isNearBottom && !loader && hasMore) {
                setState((prev) => ({ ...prev, page: prev.page + 1 }));
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loader, hasMore]);

    const [drawerStates, setDrawerStates] = useState({
        assignIncidentDrawer: false,
        filter: false,
    });

    const [prefillIncident, setPrefillIncident] = useState(null);

    const [propPage, setPropPage] = useState(1);
    const [propOptions, setPropOptions] = useState([]);
    const [hasMoreProps, setHasMoreProps] = useState(true);
    const [propLoading, setPropLoading] = useState(false);

    const fetchProperties = async (page) => {
        if (page > 1 && !hasMoreProps) return;
        setPropLoading(true);
        try {
            let res = await ApiCaller.Get(`/properties?search=&perPage=25&page=${page}&sortBy=`);
            const props = res?.data?.data?.map(p => ({ label: p.name, value: p.id })) || [];
            const paginate = res?.data?.paginate;
            if (page === 1) {
                setPropOptions(props);
            } else {
                setPropOptions(prev => [...prev, ...props]);
            }
            if (paginate) {
                setHasMoreProps(paginate.currentPage < paginate.lastPage);
            } else {
                setHasMoreProps(false);
            }
        } catch (e) {} finally {
            setPropLoading(false);
        }
    };

    useEffect(() => {
        if (drawerStates.filter && propOptions.length === 0) {
            fetchProperties(1);
        }
    }, [drawerStates.filter]);

    const onPropScroll = (e) => {
        const { target } = e;
        if (target.scrollTop + target.offsetHeight + 10 >= target.scrollHeight) {
            if (hasMoreProps && !propLoading) {
                const next = propPage + 1;
                setPropPage(next);
                fetchProperties(next);
            }
        }
    };

    const { data: incidentsOptions } = useQuery('incident', getAllIncidents, {
        enabled: drawerStates.filter,
    });

    const assignIncidentHandler = () => {
        setDrawerStates({ ...drawerStates, assignIncidentDrawer: true });
    };

    // search handler
    const searchInputHandler = (e) => {
        setSearchTerm(e.target.value);
    };

    const onCloseDrawer = () => {
        if (drawerStates?.assignIncidentDrawer) {
            setDrawerStates({ ...drawerStates, assignIncidentDrawer: false });
        } else if (drawerStates.filter) {
            setDrawerStates({ ...drawerStates, filter: false });
        }
    };

    const filterHandler = () => {
        setDrawerStates({ ...drawerStates, filter: true });
    };

    const getActionItems = (record) => {
        const actionItems = [
            {
                label: 'View Details',
                key: 2,
                icon: <FaEye color="#7BA8FF" />,
                backgroundColor: '#EEF4FF',
                color: '#7BA8FF',
            },
        ];

        if (record?.status === 'pending') {
            actionItems.unshift({
                label: 'Assign',
                key: 1,
                icon: <MdOutlineTaskAlt color='#389F4F' />,
                backgroundColor: '#D7ECDC',
                color: '#389F4F',
            });
        }

        return actionItems;
    };

    const actionHandler = (e, record) => {
        if (e?.key == 1) {
            setPrefillIncident(record);
            setDrawerStates({ ...drawerStates, assignIncidentDrawer: true });
            return;
        }

        if (e?.key == 2) {
            navigate(`/security-issues/manage-incidents/report/${record?.id}`);
        }
    };

    // filter search handler
    const incidentFilters = () => {
        let values = Object.values(pendingFilters).some((item) => item !== '');
        if (!values) {
            return;
        }

        let filterDrawer = { ...pendingFilters };
        if (filterDrawer?.status == 'pending') {
            delete filterDrawer.level;
        }

        sessionStorage.setItem('incidentFilters', JSON.stringify(pendingFilters));
        setState((prev) => ({ ...prev, page: 1, filterDrawer }));
        setDrawerStates({ ...drawerStates, filter: false });
    };

    // Filter states cleaner
    const clearFilters = () => {
        if (drawerStates.filter) {
            const emptyFilters = {
                property: '',
                incidentType: '',
                status: '',
                level: '',
                startingDate: '',
                endingDate: '',
            };
            setPendingFilters(emptyFilters);
            sessionStorage.removeItem('incidentFilters');
            setState((prev) => ({ ...prev, page: 1, filterDrawer: emptyFilters }));
            setDrawerStates({ ...drawerStates, filter: false });
        }
        return;
    };

    // the API mixes camelCase and snake_case on the user objects it embeds
    // (assignedTo.firstName on the detail screen, assignedBy.first_name), so read both
    const getAssignedToName = (record) => {
        const assignee =
            record?.assignedTo || record?.assignedUser || record?.assignee;
        const firstName = assignee?.firstName || assignee?.first_name;
        const lastName = assignee?.lastName || assignee?.last_name;
        const fullName = `${firstName || ''} ${lastName || ''}`.trim();
        return fullName || '-';
    };

    const incidentsColumn = [
        {
            title: 'Report No.',
            dataIndex: 'reportNumber',
            align: 'left',
            sorter: true,
            render: (text, record) => (
                <Row>
                    <Col lg={24}>
                        <span style={{
                            display: 'inline-block',
                            background: colorPrimary + '18',
                            color: colorPrimary,
                            border: `1px solid ${colorPrimary}44`,
                            borderRadius: 5,
                            padding: '1px 10px',
                            fontWeight: 600,
                            fontSize: 12,
                            letterSpacing: 0.4,
                            whiteSpace: 'nowrap',
                        }}>
                            {record?.reportNumber || record?.name || '-'}
                        </span>
                    </Col>
                </Row >
            ),
        },
        // {
        //     title: 'Incident Id',
        //     dataIndex: 'id',
        //     align: 'center',
        //     render: (text, record) => (
        //         <Row>
        //             <Col lg={24} className="">
        //                 {record?.id}
        //             </Col>
        //         </Row>
        //     ),
        // },
        {
            title: 'Location Name',
            dataIndex: 'property',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {record?.property?.name ? record?.property?.name : '-'}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Reported Issue',
            dataIndex: 'incidentType',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {record?.incidentType?.name
                            ? record?.incidentType?.name
                            : '-'}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Create Date',
            dataIndex: 'createdDate',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {record?.createdDate
                            ? DateTimeComponent(record?.createdDate, 'date')
                            : '-'}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Created By',
            dataIndex: 'reportedBy',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {record?.reportedBy?.firstName
                            ? record?.reportedBy?.firstName +
                            ' ' +
                            record?.reportedBy?.lastName
                            : '-'}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {getAssignedToName(record)}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Level',
            dataIndex: 'level',
            align: 'left',
            render: (text, record) => (
                <Row>
                    <Col lg={24} className="">
                        {record?.incidentType?.level
                            ? record?.incidentType?.level
                            : '-'}
                    </Col>
                </Row>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'left',
            render: (text, record) => {
                const isPending = record?.status === 'pending';
                return (
                    <Row>
                        <Col lg={24} className="">
                            <span style={{
                                display: 'inline-block',
                                background: isPending ? '#FDEEEE' : '#D7ECDC',
                                color: isPending ? Colors.Error : '#389F4F',
                                borderRadius: 5,
                                padding: '2px 10px',
                                fontWeight: 600,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                            }}>
                                {isPending ? 'Unassigned' : record?.status ? 'Assigned' : '-'}
                            </span>
                        </Col>
                    </Row>
                );
            },
        },
        {
            title: 'Action',
            dataIndex: '',
            align: 'left',
            render: (text, record) => (
                <Row onClick={(e) => e.stopPropagation()}>
                    <Col lg={24} className="">
                        <Actiondropdown
                            items={getActionItems(record)}
                            onClick={(e) => actionHandler(e, record)}
                        />
                    </Col>
                </Row>
            ),
        },
    ];

    // Handle ant-design table sort changes
    const handleSortChange = (_pagination, _filters, sorter, extra) => {
        if (extra?.action === 'sort' || (sorter && sorter.field)) {
            const tempSortBy = sorter?.field === 'reportNumber' ? 'reportNumber' : 'createdAt';
            const tempSortOrder = sorter?.order === 'ascend' ? 'asc' : 'desc';

            if (tempSortBy !== state.sortBy || tempSortOrder !== state.sortOrder) {
                setState((prev) => ({ ...prev, sortBy: tempSortBy, sortOrder: tempSortOrder, page: 1 }));
            }
        }
    };

    // in case of status pending we are disabling the level fields.
    const valueTrackHandler = (value) => {
        if (value == 'pending') {
            setIsLevelDisabled(true);
            setPendingFilters((prev) => ({ ...prev, level: '' }));
        } else {
            setIsLevelDisabled(false);
        }
    }


    return (
        <>
            <div className="w-full flex items-center justify-between gap-3 my-4" style={{ flexWrap: 'wrap' }}>
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                    <div
                        className="flex items-center gap-2 cursor-pointer shrink-0"
                        onClick={() => navigate('/security-issues')}
                        style={{ color: '#555', userSelect: 'none' }}
                    >
                        <IoArrowBack size={18} />
                        {/* <span>Back</span> */}
                    </div>
                    <h1 className="text-lg md:text-xl font-semibold m-0">Security Issues / Manage Incidents/Issues</h1>
                </div>

                <div className="flex items-center gap-3 flex-wrap justify-end" style={{ flexShrink: 0 }}>
                    <div style={{ width: '260px' }}>
                        <TextField.Basic
                            style={{ background: '#eff2f5', width: '100%', margin: 0 }}
                            suffix={<SearchOutlined style={{ fontSize: '18px', color: '#888' }} />}
                            placeholder="Search..."
                            onChange={(e) => searchInputHandler(e)}
                            id="search"
                            name="search"
                            value={searchTerm}
                            className="placeholder-red outline-none w-full py-2 px-4"
                        />
                    </div>

                    {(userRole === 'admin' || userRole === 'manager') && (
                        <>
                        <Button.Basic
                            className="text-white outline-none border-none cursor-pointer"
                            style={{
                                height: '40px',
                                flexShrink: 0,
                                background: colorPrimary,
                                color: Colors.White,
                                paddingLeft: '20px',
                                paddingRight: '20px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                letterSpacing: '0.3px',
                            }}
                            text="Assign Incident"
                            icon={<PlusOutlined />}
                            onClick={() => assignIncidentHandler && assignIncidentHandler()}
                        />
                        <FaFilter
                                            size={40}
                                            color="white"
                                            style={{ background: colorPrimary }}
                                            className="cursor-pointer p-2 rounded-md"
                                            onClick={filterHandler}
                                        />
                                        </>
                    )}
                </div>
            </div>

            <Row
                gutter={[8, 32]}
                className="w-fit sm:w-fit lg:w-fit xl:w-full"
                style={{
                    background: 'white',
                    // padding: '16px',
                    borderRadius: '10px',
                }}
            >
                <Col lg={24}>
                    <Row className="w-full">
                        <Col lg={24}>
                            <TableWrapper
                                tableStyle={{
                                    borderRadius: '40px',
                                    width: '100%',
                                }}
                                headerbg={'red'}
                                tableColumns={incidentsColumn}
                                title={() => (
                                    <div className="flex items-center justify-between px-2">
                                        {/* <span className="text-lg font-bold">
                                            Manage Incidents/Issues
                                        </span> */}
                                        {/* <img
                                            src={filter}
                                            height={35}
                                            width={35}
                                            className="cursor-pointer"
                                            onClick={filterHandler}
                                        /> */}
                                        {/* <FaFilter
                                            size={35}
                                            color="white"
                                            style={{ background: colorPrimary }}
                                            className="cursor-pointer p-2 rounded-md"
                                            onClick={filterHandler}
                                        /> */}
                                    </div>
                                )}
                                tableData={accumulatedIncidents}
                                pagination={false}
                                onChange={handleSortChange}
                                rowClassName={'row-antd'}
                                onRow={(record) => ({
                                    onClick: (e) => {
                                        // Ignore clicks on action dropdown elements to prevent double triggers
                                        if (e.target.closest('.ant-dropdown-trigger') || e.target.closest('a')) {
                                            return;
                                        }
                                        navigate(`/security-issues/manage-incidents/report/${record?.id}`);
                                    },
                                    style: { cursor: 'pointer' },
                                })}
                            />
                            {loader && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                                    <Spin />
                                </div>
                            )}
                            <ScrollEndMessage
                                hasMore={hasMore}
                                loading={loader}
                                dataLength={accumulatedIncidents?.length}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <AssignIncidentDrawer
                drawerStates={drawerStates}
                setDrawerStates={setDrawerStates}
                onClose={onCloseDrawer}
                prefillIncident={prefillIncident}
                onPrefillHandled={() => setPrefillIncident(null)}
                onSuccess={() => {
                    setAccumulatedIncidents([]);
                    dispatch(ManageIncidentsAction.GetManageIncidents({ ...state, page: 1 }));
                }}
            />

            <BasicDrawer
                openDrawer={drawerStates?.filter}
                onClose={onCloseDrawer}
                width={300}
                title={'Filter'}
                children={
                    <>
                        <Row gutter={[1, 16]} className="w-full h-full">
                            <Col span={24}>
                                <label
                                    className="font-normal"
                                    style={{ color: Colors.LabelColor }}
                                >
                                    Property
                                </label>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Select: {
                                                optionSelectedBg: colorPrimary,
                                                optionActiveBg:
                                                    Colors.LightGray,
                                                optionSelectedColor:
                                                    Colors.White,
                                                selectorBg: Colors.LightGray,
                                            },
                                        },
                                    }}
                                >
                                    <Select
                                        allowClear={true}
                                        className="w-full h-9 outline-none rounded"
                                        value={pendingFilters?.property}
                                        onChange={(value) => {
                                            if (
                                                value !== undefined &&
                                                value !== null
                                            ) {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    property: value,
                                                }));
                                            } else {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    property: '',
                                                }));
                                            }
                                        }}
                                        options={propOptions}
                                        onPopupScroll={onPropScroll}
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                {propLoading && <div style={{ textAlign: 'center', padding: '8px 0' }}><Spin size="small" /></div>}
                                            </>
                                        )}
                                    />
                                </ConfigProvider>
                            </Col>

                            <Col span={24}>
                                <label
                                    className="font-normal"
                                    style={{ color: Colors.LabelColor }}
                                >
                                    Incident Type
                                </label>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Select: {
                                                optionSelectedBg: colorPrimary,
                                                optionActiveBg:
                                                    Colors.LightGray,
                                                optionSelectedColor:
                                                    Colors.White,
                                                selectorBg: Colors.LightGray,
                                            },
                                        },
                                    }}
                                >
                                    <Select
                                        allowClear={true}
                                        className="w-full h-9 outline-none rounded"
                                        value={pendingFilters?.incidentType}
                                        onChange={(value) => {
                                            if (
                                                value !== undefined &&
                                                value !== null
                                            ) {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    incidentType: value,
                                                }));
                                            } else {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    incidentType: '',
                                                }));
                                            }
                                        }}
                                        options={
                                            incidentsOptions
                                                ? incidentsOptions
                                                : []
                                        }
                                    />
                                </ConfigProvider>
                            </Col>

                            <Col span={24}>
                                <label
                                    className="font-normal"
                                    style={{ color: Colors.LabelColor }}
                                >
                                    Status
                                </label>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Select: {
                                                optionSelectedBg: colorPrimary,
                                                optionActiveBg:
                                                    Colors.LightGray,
                                                optionSelectedColor:
                                                    Colors.White,
                                                selectorBg: Colors.LightGray,
                                            },
                                        },
                                    }}
                                >
                                    <Select
                                        allowClear={true}
                                        className="w-full h-9 outline-none rounded"
                                        value={pendingFilters?.status}
                                        onChange={(value) => {
                                            if (
                                                value !== undefined &&
                                                value !== null
                                            ) {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    status: value,
                                                }));
                                                valueTrackHandler(value)
                                            } else {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    status: '',
                                                }));
                                                valueTrackHandler(value)
                                            }
                                        }}
                                        options={[
                                            {
                                                value: 'pending',
                                                label: 'Pending',
                                            },
                                            {
                                                value: 'assigned',
                                                label: 'Assigned',
                                            },
                                        ]}
                                    />
                                </ConfigProvider>
                            </Col>

                            <Col span={24}>
                                <label
                                    className="font-normal"
                                    style={{ color: Colors.LabelColor }}
                                >
                                    Level
                                </label>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Select: {
                                                optionSelectedBg: colorPrimary,
                                                optionActiveBg:
                                                    Colors.LightGray,
                                                optionSelectedColor:
                                                    Colors.White,
                                                selectorBg: Colors.LightGray,
                                            },
                                        },
                                    }}
                                >
                                    <Select
                                        allowClear={true}
                                        className="w-full h-9 outline-none rounded"
                                        value={pendingFilters?.level}
                                        disabled={isLevelDisabled}
                                        onChange={(value) => {
                                            if (
                                                value !== undefined &&
                                                value !== null
                                            ) {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    level: value,
                                                }));
                                            } else {
                                                setPendingFilters((prev) => ({
                                                    ...prev,
                                                    level: '',
                                                }));
                                            }
                                        }}
                                        options={[
                                            {
                                                value: '3',
                                                label: 'High',
                                            },
                                            {
                                                value: '1',
                                                label: 'Low',
                                            },
                                            {
                                                value: '2',
                                                label: 'Medium',
                                            },
                                        ]}
                                    />
                                </ConfigProvider>
                            </Col>

                            <Col span={24}>
                                <label
                                    className="font-normal"
                                    style={{ color: Colors.LabelColor }}
                                >
                                    Date Range
                                </label>
                                <RangeDatePicker
                                    allowClear={true}
                                    format={dateFormat}
                                    defaultValue={
                                        pendingFilters?.startingDate
                                            ? [
                                                dayjs(
                                                    pendingFilters
                                                        ?.startingDate,
                                                ),
                                                dayjs(
                                                    pendingFilters
                                                        ?.endingDate,
                                                ),
                                            ]
                                            : [dayjs(), dayjs()]
                                    }
                                    onChange={(dates, dateStrings) => {
                                        if (dateStrings[0]) {
                                            setPendingFilters((prev) => ({
                                                ...prev,
                                                startingDate: dateStrings[0],
                                                endingDate: dateStrings[1],
                                            }));
                                        } else {
                                            setPendingFilters((prev) => ({
                                                ...prev,
                                                startingDate: '',
                                                endingDate: '',
                                            }));
                                        }
                                    }}
                                // disabledDate={(current) => current.isBefore(moment().subtract(1, "day"))}
                                />
                            </Col>

                            <Row
                                className="w-full flex justify-between gap-4 px-2"
                                align={'middle'}
                            >
                                <Col span={11}>
                                    <Button.Basic
                                        onClick={incidentFilters}
                                        style={{
                                            color: 'white',
                                            backgroundColor: colorPrimary,
                                        }}
                                        text={'Search'}
                                        className={'w-full rounded'}
                                        size={'large'}
                                    />
                                </Col>
                                <Col span={11}>
                                    <Button.Basic
                                        onClick={clearFilters}
                                        style={{
                                            color: 'white',
                                            backgroundColor: Colors.Error,
                                        }}
                                        text={'Clear'}
                                        className={'w-full rounded'}
                                        size={'large'}
                                    />
                                </Col>
                            </Row>
                        </Row>
                    </>
                }
            />
        </>
    );
};

const data = [
    {
        id: 1,
        name: 'zaid',
        issue: 'technical',
        createdDate: '12/12/2024',
        createdBy: 'zaid',
        level: 'high',
    },
];

export default ManageIncidents;
